/** Wire-shape normalizer for GET/PUT `/api/user-management/v1/settings/change-categories`. */

import type { ChangeCategoryDto, ChangeCategoryListDto } from '@/userManagement/application/types/UserManagementApiContracts';

function readString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function readNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function readBoolean(value: unknown): boolean {
  return value === true;
}

function normalizeItem(raw: unknown): ChangeCategoryDto | null {
  if (raw == null || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const code = readString(row.code ?? row.Code).trim();
  if (code === '') return null;

  return {
    id: readNumber(row.id ?? row.Id),
    code,
    displayName: readString(row.displayName ?? row.DisplayName).trim() || code,
    sortOrder: readNumber(row.sortOrder ?? row.SortOrder),
    isActive: readBoolean(row.isActive ?? row.IsActive),
    isSystemDefault: readBoolean(row.isSystemDefault ?? row.IsSystemDefault)
  };
}

/**
 * Accepts `{ items: [...] }`, `{ Items: [...] }`, or a bare array.
 * Always returns sorted-by-sortOrder items.
 */
export function normalizeChangeCategoryList(raw: unknown): ChangeCategoryListDto {
  let list: unknown[] = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else if (raw != null && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    const items = obj.items ?? obj.Items;
    if (Array.isArray(items)) list = items;
  }

  const items = list
    .map(normalizeItem)
    .filter((item): item is ChangeCategoryDto => item != null)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code));

  return { items };
}
